import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Printer } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

const hhmm = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

/** Escolha manual da impressora. Sem fila e sem distribuição automática. */
export function StartProductionModal({ production, onClose, onStarted }) {
  const [data, setData] = useState(null);
  const [printerId, setPrinterId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/printers/availability")
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const start = async () => {
    if (!printerId) return toast.error("Escolha a impressora que vai executar esta produção.");
    setBusy(true);
    try {
      await api.put(`/productions/${production.id}/status`, {
        status: "EM_PRODUCAO",
        printer_id: printerId,
      });
      toast.success("Produção iniciada.");
      onStarted();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
      setPrinterId("");
      load();
    } finally {
      setBusy(false);
    }
  };

  const free = (data?.printers || []).filter((p) => p.available);
  const taken = (data?.printers || []).filter((p) => !p.available);

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Iniciar produção"
      description={`${production.product_name}${
        production.variation_name ? ` · ${production.variation_name}` : ""
      } · ${production.quantity_needed} un`}
      testid="start-production-modal"
    >
      {!data ? (
        <Loading />
      ) : (
        <div className="mt-2 space-y-4">
          {data.all_busy && (
            <div
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
              data-testid="all-printers-busy-warning"
            >
              <p className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle className="h-4 w-4" /> Todas as impressoras estão ocupadas
              </p>
              <p className="mt-1">
                Não há nenhuma impressora disponível para iniciar esta produção. A produção
                continua aguardando.
              </p>
            </div>
          )}

          {free.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-emerald-300">
                Disponíveis ({free.length})
              </p>
              {free.map((p) => (
                <button
                  key={p.printer_id}
                  type="button"
                  onClick={() => setPrinterId(p.printer_id)}
                  data-testid={`printer-option-${p.printer_id}`}
                  className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                    printerId === p.printer_id
                      ? "border-brand bg-brand/15 text-brand"
                      : "border-white/10 bg-secondary/40 hover:border-white/25"
                  }`}
                >
                  <Printer className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 truncate">{p.name}</span>
                </button>
              ))}
            </div>
          )}

          {taken.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-red-300">
                Ocupadas ({taken.length})
              </p>
              {taken.map((p) => (
                <div
                  key={p.printer_id}
                  className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-xs"
                  data-testid={`printer-busy-${p.printer_id}`}
                >
                  <p className="flex items-center gap-1.5 text-sm font-medium text-red-200">
                    <AlertTriangle className="h-3.5 w-3.5" /> {p.name}
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    A impressora {p.name} já está executando outra produção.
                  </p>
                  <p className="mt-1">
                    Produção atual: {p.current_production?.name || "—"} · Quantidade:{" "}
                    <span className="font-num">{p.current_production?.quantity ?? "—"}</span> ·
                    Iniciada em <span className="font-num">{hhmm(p.current_production?.started_at)}</span>
                  </p>
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground">
                Verifique se a impressão anterior já foi finalizada ou escolha outra impressora.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-white/10 pt-4">
            <Button
              variant="ghost"
              onClick={onClose}
              className="h-11 rounded-full px-5"
              data-testid="start-production-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={start}
              disabled={busy || !printerId}
              className="h-11 rounded-full px-6 font-semibold"
              data-testid="start-production-confirm-btn"
            >
              {busy ? "Iniciando..." : "Iniciar produção"}
            </Button>
          </div>
        </div>
      )}
    </ResponsiveModal>
  );
}
